package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.site.components.NavIcon
import dev.lingvo.site.ds.LCardLink
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSettingsIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.nav.AppNav
import dev.lingvo.site.nav.NavGlyph
import dev.lingvo.site.pages.app.projects.slug.settings.cardGridModifier
import dev.lingvo.site.state.WorkspaceSelection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The index every settings URL implies.
 *
 * `/app/settings/general` existed and `/app/settings` did not, so anyone who shortened the URL — or
 * followed a link that dropped the last segment — got a 404 from a product that plainly has
 * settings. A parent path that 404s reads as "this whole area is broken", not "you missed a word".
 *
 * **Drawn from `AppNav`, not from thirty hand-written links.** The two lists disagreed, and the
 * disagreement was invisible from either side: this page linked to Seats, Spend requests, Budgets
 * and Notifications, which the nav model had never heard of — so the pill row above each of those
 * pages could not contain the page you were standing on, and the command palette (which reads the
 * model) could not offer them at all. It also silently role-gated its own way: `manages` hid a
 * block of five entries here while `AppNav.settingsFor` decided the same question per destination,
 * and only one of the two answers was the one the server asks. Now there is one answer.
 *
 * Each entry says what it is *for*, because "Providers" and "Agents" mean nothing to somebody who
 * has not already read the documentation. Those sentences are the one thing the model does not
 * hold — it holds names, roles and marks — so they live in [settingsHubDescription], keyed by the
 * same destination id.
 */
@Page("/app/settings")
@Layout(".components.layouts.AppLayout")
@Composable
fun SettingsIndexPage() {
    val workspace = WorkspaceSelection.current()
    val role = workspace?.role
    val manages = role != null && role.atLeast(Role.ADMIN)

    Column(Modifier.fillMaxWidth().gap(Space.XL).maxWidth(1128.px)) {
        PageHeader(
            t("app.settings.index.settings", "Settings"),
            // Was a raw English `+` expression with the workspace name spliced in, so the one page
            // whose whole job is navigation introduced itself in English in every locale.
            workspace?.name?.let {
                tf(
                    "app.settings.index.everything_about_workspace_and_about_you",
                    "Everything about {workspace}, and about you.",
                    "workspace" to it,
                )
            } ?: t("app.settings.index.everything_here", "Everything here."),
            mark = { LSettingsIcon() },
        )

        AppNav.settingsHubGroupsFor(role).forEach { (label, destinations) ->
            Group(t(AppNav.navGroupKey(label), label), settingsHubGroupGlyph(label)) {
                destinations.forEach { destination ->
                    Entry(
                        path = destination.path,
                        // Keyed by id, exactly as the rail and the pill row do it — the model's
                        // labels are class-init `val`s and would freeze in English if translated
                        // there.
                        title = t("nav.${destination.catalogueKey}", destination.label),
                        description = settingsHubDescription(destination.id),
                        glyph = destination.glyph,
                    )
                }
            }
        }

        Group(t("app.settings.index.how_this_works", "How this works"), NavGlyph.BookOpen) {
            // `/docs` is linked from the marketing hero and from `/app/start`, and from nowhere else
            // inside the signed-in app — so somebody who set the product up last month and now wants
            // the CLI's flags has to remember a URL. This hub is where they look for it. It is not a
            // settings destination, so it is the one entry that is still written out here.
            Entry(
                path = "/docs",
                title = t("app.settings.index.documentation", "Documentation"),
                description = t(
                    "app.settings.index.installing_the_cli_the_sync_loop",
                    "Installing the CLI, the sync loop, and every file format.",
                ),
                glyph = NavGlyph.File,
            )
        }

        if (!manages) {
            // Absent rather than disabled, and said plainly so nobody hunts for a menu that is not
            // being hidden from them so much as not theirs.
            LMuted(t("app.settings.index.billing_providers_and_agents_are_managed", "Billing, providers and agents are managed by this workspace's owners and admins."))
        }
    }
}

/**
 * What each settings page is for, in one sentence.
 *
 * Keyed by the destination id rather than written beside the link, so the hub's structure comes
 * from the nav model and only the prose lives here. A destination with no sentence yet renders its
 * name alone rather than an apology or a placeholder.
 */
@Composable
private fun settingsHubDescription(id: String): String? = when (id) {
    "general" -> t("app.settings.index.its_name_and_how_to_leave", "Its name, and how to leave, hand it over or delete it.")
    "workspaces" -> t("app.settings.index.every_workspace_you_belong_to", "Every workspace you belong to.")
    "members" -> t("app.settings.index.who_is_here_what_they_may", "Who is here, what they may do, and who costs a seat.")
    "providers" -> t("app.settings.index.the_keys_machine_translation_uses", "The keys machine translation uses.")
    "audit" -> t("app.settings.index.what_happened_to_this_workspace_and", "What happened to this workspace, and who did it.")
    "webhooks" -> t("app.settings.index.telling_your_systems_when_a_language", "Telling your systems when a language is finished.")
    "billing" -> t("app.settings.index.the_plan_the_card_and_what", "The plan, the card, and what is being charged.")
    "seats" -> t("app.settings.index.who_is_billable_and_what_each", "Who is billable, and what each one costs.")
    "usage" -> t("app.settings.index.machine_translation_credits_this_month", "Machine-translation credits this month.")
    "budgets" -> t("app.settings.index.what_can_cost_money_while_nobody", "What can cost money while nobody is looking.")
    "spend" -> t("app.settings.index.where_the_credits_went_by_language", "Where the credits went, by language and provider.")
    "agents" -> t("app.settings.index.credentials_for_tools_and_fleets_that", "Credentials for tools and fleets that work here without a person.")
    "approvals" -> t("app.settings.index.agents_asking_to_spend_past_their", "Agents asking to spend past their budget.")
    "tokens" -> t("app.settings.index.credentials_for_the_cli_and_your", "Credentials for the CLI and your own scripts.")
    "account" -> t("app.settings.index.your_name_and_the_address_you", "Your name and the address you sign in with.")
    "security" -> t("app.settings.index.password_and_the_devices_signed_in", "Password, and the devices signed in as you.")
    "notifications" -> t("app.settings.index.what_reaches_your_inbox", "What reaches your inbox.")
    "close" -> t("app.settings.index.leaving_for_good_and_what_happens", "Leaving for good, and what happens to what you own.")
    "help" -> t("app.settings.index.getting_unstuck_and_how_to_reach", "Getting unstuck, and how to reach us.")
    else -> null
}

/**
 * Which glyph marks each group heading.
 *
 * Keyed by the model's English label, exactly as [AppNav.navGroupKey] is — the group lists are
 * class-init `val`s, so a glyph stored beside them could not be read from here, and a `when` over
 * the translated string would stop matching in the first locale that ships.
 *
 * "Section headings carry a glyph": the H1 above these is marked and all twenty cards below them
 * are, so five bare bold words were the only unmarked tier on the one page whose whole job is
 * being navigated. An unknown group ("More", the fallback for a destination no group claims) takes
 * the grid mark rather than none, so the tier stays consistent.
 */
private fun settingsHubGroupGlyph(label: String): NavGlyph = when (label) {
    "Workspace" -> NavGlyph.Settings
    "Money" -> NavGlyph.Coins
    "Automation" -> NavGlyph.Bot
    "You" -> NavGlyph.User
    else -> NavGlyph.Grid
}

@Composable
private fun Group(title: String, glyph: NavGlyph, content: @Composable () -> Unit) {
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        // A real heading, so every group on the hub appears in a screen reader's heading list.
        // As bold text none of them did, and the one page in the product whose whole job is to
        // be navigated offered a single H1 and thirty links to arrow through.
        //
        // The mark is decorative — `LSectionTitle` hides it — because the word beside it is the
        // heading a screen reader announces.
        LSectionTitle(title, icon = { NavIcon(glyph) })
        // **`auto-fill`, not `auto-fit`.** `auto-fit` collapses the tracks a group does not fill
        // and stretches the survivors, so Automation's two cards were 1114px wide under
        // Workspace's three 734px ones and the page ran down three different column grids with
        // seams that lined up with nothing. `auto-fill` keeps the empty tracks, so every card on
        // the hub is the same width and there is one ladder down the page.
        Column(
            cardGridModifier("settings-hub").styleModifier {
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
            },
        ) { content() }
    }
}

/**
 * One destination, as a card rather than a line.
 *
 * Thirty stacked links with a sentence under each is a wall: nothing about it says which of the
 * five groups you are in, and the mark — the fastest identifier on the row — was a 16px glyph
 * beside 14px text. A card gives the mark room, gives the sentence a boundary, and makes the whole
 * tile the hit target, which is what somebody arriving on a phone is aiming at.
 *
 * **The card is the anchor, not a container for one.** This used to be `LCard { … Link(title) … }`
 * — a card-shaped `<div>` with a smaller link glued to its title — so the description, the mark and
 * the rest of the tile's own padding did nothing on click, and `LCardStyle`'s hover-lift never fired
 * because the element it lifts (`:is(a, button):hover`) is the card itself, not a link nested inside
 * it. `LCardLink` makes the whole tile the one `<a>`: every pixel of it is the hit target, it is one
 * stop in the tab order instead of a decoration around a smaller one, and the lift rule now applies
 * to the thing it was written for.
 */
@Composable
internal fun Entry(path: String, title: String, description: String?, glyph: NavGlyph) {
    LCardLink(path, Modifier.height(100.percent)) {
        Column(Modifier.fillMaxWidth().gap(Space.XS)) {
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                // Decorative: the title beside it carries the name, and a mark that announced
                // itself would read every row on this page twice.
                Span(Modifier.attr("aria-hidden", "true").toAttrs()) { NavIcon(glyph) }
                // A plain span, not a nested link — the whole card is already the anchor.
                // `data-card-title` is what `LCardLinkStyle` underlines on hover, so the name
                // signals "this is a link" without colouring the whole tile in an accent.
                Span(
                    Modifier.fontSize(TypeScale.BodySmall).fontWeight(600).toAttrs {
                        attr("data-card-title", "")
                    },
                ) { Text(title) }
            }
            description?.let { LMuted(it) }
        }
    }
}
